// The agent display names, one per tile type.
//
// Extracted from `ChatPanel.js` so the DASHBOARD can use them too. That is not a
// tidiness move: `ChatPanel` cannot be rendered under jest (it pulls `api/chat`,
// `chatRounds`, `interactionLog`, `useConfirm`, `useToast`), while `Dashboard` is
// rendered in `Dashboard.test.js` — so anything the dashboard imports must not
// drag ChatPanel in behind it. This module imports nothing at all.
//
// The dashboard's per-subagent activity list keys off the same table, so a module
// is named identically whether you are reading its own chat panel or watching it
// work from the dashboard.
//
// Every agent is Dennis wearing a different hat — `Dennis (Mesh)`, `Dennis (CAD)`
// — rather than a separate "{Thing} Assistant" persona per module. The topic is
// the ONLY part that varies, so it is the only part stored: writing the 39 full
// strings out is how the product name drifts in one row and not the others.
// `ChatPanel` renders each of them beside the brand mark from
// `public/HIDENN_favicon.png`, which is why none of these carries an emoji.

// The parenthetical half. Deliberately its own vocabulary, distinct from
// `NODE_TEMPLATES[…].data.label` in `pages/workflowGraph.js` ("Mechanical
// BC/Load", "Build Plate Planning") — that names the MODULE, this names the
// agent working on it, and it stays short enough to sit inside brackets in a
// 13px header.
export const TILE_TOPICS = {
  cadInput: 'CAD',
  assemblyInput: 'Assembly',
  mesh: 'Mesh',
  bc: 'BC',
  contact: 'Contact',
  material: 'Materials',
  solver: 'Solver',
  surfaceMesh: 'Surface Mesh',
  itdSolver: 'ITD Solver',
  surrogateModel: 'Surrogate',
  export: 'Export',
  sptInput: 'SPT',
  sptSolver: 'SPT Solver',
  sptPredictor: 'SPT Predictor',
  lpbfToolpath: 'LPBF Toolpath',
  dedToolpath: 'DED Toolpath',
  amInput: 'AM Input',
  amSolver: 'AM Solver',
  amStudy: 'AM Study',
  amSurrogate: 'AM Surrogate',
  defectNet: 'Defect Net',
  amCalibration: 'AM Calibration',
  // Every tile type has a real subagent, so every tile type gets a topic — an
  // unlisted one falls back to a bare "Dennis", which is not wrong, only less
  // useful than it could be.
  fluidMesh: 'Fluid Mesh',
  flowBc: 'Flow BC',
  flowThermalBc: 'Flow Thermal BC',
  fluidMaterial: 'Fluid Material',
  fluidSolver: 'Fluid Solver',
  thermalFlowSolver: 'Thermal Flow Solver',
  flowViz: 'Flow Viz',
  thermalBc: 'Thermal BC',
  thermalMaterial: 'Thermal Materials',
  thermalSolver: 'Thermal Solver',
  thermalSurrogate: 'Thermal Surrogate',
  spinDownSolver: 'Spin-Down Solver',
  gearContactSolver: 'Gear Contact Solver',
  thermomechanicalSolver: 'Thermomechanical Solver',
  thermomechanicalSurrogate: 'Thermomechanical Surrogate',
  designOptimization: 'Design Optimization',
  propertyEstimator: 'Property Estimator',
  buildPlate: 'Build Plate',
  trackTest: 'Track Test',
  amInitialCondition: 'AM Initial Condition',
  amDigitalExperiment: 'AM Digital Experiment',
  amViz: 'AM Visualization',
  controlStudy: 'Control Study',
  dynamicSurrogate: 'Dynamic Surrogate',
  pidTuning: 'PID Tuning',
};

export const TILE_LABELS = {
  // The workflow agent is the product itself, so it wears no hat: it is the one
  // agent whose scope is the whole canvas rather than one module.
  workflow: 'Dennis',
  ...Object.fromEntries(
    Object.entries(TILE_TOPICS).map(([tileType, topic]) => [tileType, `Dennis (${topic})`])
  ),
};

// A type with no topic is still Dennis — the product, not a gap. (The old
// fallback was a bare "🤖 Assistant", which read as unfinished.)
export const DEFAULT_TILE_LABEL = 'Dennis';

/**
 * The agent name for a tile type, or the generic fallback.
 *
 * The activity list needs a name for a row it has only a `tileType` for, and
 * `TILE_LABELS[undefined]` would render "undefined".
 */
export function tileLabel(tileType) {
  return TILE_LABELS[tileType] || DEFAULT_TILE_LABEL;
}
